import { optimizedRetryOption } from "o4a-react";
import * as React from "react";
import apiClients from "../../apiClients";
import { ttlOneMinCaching } from "../../constants/uiConstants";
import { parseId } from "../../helpers/idHelper";
import { OciResourceType } from "../../helpers/resourceHelper";
import { useQueryCall } from "../../hooks/useQueryCall";
import { AzureSubscriptionSummaryExt } from "../../hooks/useSubscriptions";
import { getOciRegion } from "../../utils";
import { OciLink, OciLinkProps } from "./OciLink";

export interface OciVmCdbLinkProps extends Pick<OciLinkProps, "location" | "analytics"> {
  id: string;
  subscriptions: AzureSubscriptionSummaryExt[];
}

export const OciVmCdbLink = ({ id, location, subscriptions, analytics }: OciVmCdbLinkProps): JSX.Element => {
  const idComps = parseId(id);
  const resGroup = decodeURIComponent(idComps?.resourceGroup || "");
  const resourceName = decodeURIComponent(idComps?.resourceName || "");
  const subscription = subscriptions?.find(item => item.id === idComps?.subscriptionId);

  const { response: responseCdb, error: errorCdb } = useQueryCall({
    wait: !id,
    method: apiClients.withRegion(getOciRegion(location)).vmDatabaseApi.getVmdbDatabase,
    options: {
      args: {
        subscriptionId: idComps?.subscriptionId || "",
        resourceGroupName: resGroup,
        databaseName: resourceName,
      },
      caching: ttlOneMinCaching,
      retry: optimizedRetryOption,
    },
  });

  const cdb = responseCdb?.data;

  const dbSystemIdComps = parseId(cdb?.dbSystemId);
  const dbSystemResGroup = decodeURIComponent(dbSystemIdComps?.resourceGroup || "");
  const dbSystemResourceName = decodeURIComponent(dbSystemIdComps?.resourceName || "");

  const { response: responseDbSystem, error: errorDbSystem } = useQueryCall({
    wait: !cdb?.dbSystemId,
    method: apiClients.withRegion(getOciRegion(location)).vmDatabaseApi.getVmdbDbSystem,
    options: {
      args: {
        subscriptionId: dbSystemIdComps?.subscriptionId || "",
        resourceGroupName: dbSystemResGroup,
        dbSystemName: dbSystemResourceName,
      },
      caching: ttlOneMinCaching,
      retry: optimizedRetryOption,
    },
  });

  const dbSystem = responseDbSystem?.data;

  const dbHomeIdComps = parseId(cdb?.dbHomeId);
  const dbHomeResGroup = decodeURIComponent(dbHomeIdComps?.resourceGroup || "");
  const dbHomeResourceName = decodeURIComponent(dbHomeIdComps?.resourceName || "");

  const { response: responseDbHome, error: errorDbHome } = useQueryCall({
    wait: !cdb?.dbHomeId,
    method: apiClients.withRegion(getOciRegion(location)).vmDatabaseApi.getVmdbDbHome,
    options: {
      args: {
        subscriptionId: dbHomeIdComps?.subscriptionId || "",
        resourceGroupName: dbHomeResGroup,
        dbHomeName: dbHomeResourceName,
      },
      caching: ttlOneMinCaching,
      retry: optimizedRetryOption,
    },
  });

  const dbHome = responseDbHome?.data;

  const ociResourcePath = `/dbaas/dbsystems/${dbSystem?.ocid}/databases/${dbHome?.ocid}/${cdb?.ocid}`;

  return (
    <OciLink
      ociResourceType={OciResourceType.VMDB_CDB}
      ociResourcePath={!errorCdb && !errorDbHome && !errorDbSystem && dbSystem?.ocid && dbHome?.ocid && cdb?.ocid
        ? ociResourcePath : undefined}
      ociResourceName={cdb?.dbName}
      location={location}
      subscription={subscription}
      analytics={analytics}
    />
  );
};
